"use client";

import { useEffect, useState, useCallback } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { getCookie } from 'cookies-next';
import { useAuthStore } from '@/stores/authStore';

interface AuthGuardProps {
    children: React.ReactNode;
}

const PUBLIC_PATHS = ['/auth/login', '/auth/register', '/', '/auth/forgot-password', '/public/verify-email'];

export default function AuthGuard({ children }: AuthGuardProps) {
    const router = useRouter();
    const pathname = usePathname();
    const [authorized, setAuthorized] = useState(false);
    const [isChecking, setIsChecking] = useState(true);
    const { loadUser } = useAuthStore();

    const isPublicPath = useCallback((path: string | null) => {
        if (!path) return false;
        return PUBLIC_PATHS.some(publicPath => path === publicPath || path.startsWith('/public'));
    }, []);

    useEffect(() => {
        const checkAuth = () => {
            const token = getCookie('token');
            const isPublic = isPublicPath(pathname);

            if (!token && !isPublic) {
                // No token and trying to access protected route - redirect to login
                setAuthorized(false);
                setIsChecking(false);
                window.location.href = '/auth/login';
                return;
            }

            if (token && (pathname === '/auth/login' || pathname === '/auth/register')) {
                // Has token but on auth pages - redirect to dashboard
                setAuthorized(true);
                setIsChecking(false);
                router.push('/dashboard');
                return;
            }

            // Either has token on protected route, or is on public route
            setAuthorized(true);
            setIsChecking(false);

            // Load user data if there's a token
            if (token) {
                loadUser();
            }
        };

        checkAuth();

        // Listen for storage events (token removal from other tabs/401 handler)
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'token' || e.key === null) {
                checkAuth();
            }
        };

        // Also check periodically in case the 401 handler removed the token
        const interval = setInterval(() => {
            const token = getCookie('token');
            const isPublic = isPublicPath(pathname);
            if (!token && !isPublic && authorized) {
                // Token was removed while we were authorized - redirect
                window.location.href = '/auth/login';
            }
        }, 1000);

        window.addEventListener('storage', handleStorageChange);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(interval);
        };
    }, [router, pathname, loadUser, isPublicPath, authorized]);

    // Show nothing while checking auth or if not authorized on protected route
    if (isChecking) {
        return null;
    }

    // Double-check token exists before rendering protected content
    if (typeof window !== 'undefined') {
        const token = getCookie('token');
        const isPublic = isPublicPath(pathname);

        if (!token && !isPublic) {
            // Token is gone but we haven't redirected yet - hide content
            return null;
        }
    }

    if (!authorized) {
        return null;
    }

    return <>{children}</>;
}
